import { useState, type ReactNode } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import type { PreliminaryOfferReturnState } from "@/features/offer/pages/PreliminaryOfferPage/PreliminaryOfferPage";
import { OceanOfferCard, usePreliminaryOfferGeneration } from "@/features/offer";
import { routes } from "@/routes/routes";
import { BotMessage } from "../../components/BotMessage";
import { QuickReplies } from "../../components/QuickReplies";
import { TypingIndicator } from "../../components/TypingIndicator";
import { ChatTurnLayout } from "../../components/ChatTurnLayout";
import { useChatTyping } from "../../components/ChatTurnLayout/ChatTypingContext";
import { chatCopy } from "../../engine/copy";
import type { ChatTurnProps } from "../../types/chat";
import { ScreenStepTurn } from "../ScreenStepTurn";
import styles from "./PreliminaryOfferTurn.module.scss";

const RETRY = "retry";

const isOutOfSync = (state: unknown) =>
  (state as PreliminaryOfferReturnState | null)?.isPreliminaryOfferOutOfSync ===
  true;

// Shown once the bot has finished typing what comes before.
function AfterTyping({ children }: { children: ReactNode }) {
  const typing = useChatTyping();
  return typing && typing.queue.length > 0 ? null : children;
}

// The preliminary offers are priced in the chat as soon as the BC score
// allows them, and shown as its answer. Without a BC phone the step asks for
// KYC first; without a credit history there is no offer to price: straight to
// the no-score screen.
export function PreliminaryOfferTurn(props: ChatTurnProps) {
  if (props.facts.isBcPhoneMissing) {
    return <ScreenStepTurn {...props} />;
  }

  if (!props.facts.hasBcScore) {
    return <Navigate to={routes.offer.preliminaryNoScore} replace />;
  }

  return <PreliminaryOffers {...props} />;
}

function PreliminaryOffers({ isPromptTyped }: ChatTurnProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // The backend contradicted the facts the chat opened this step on: asking
  // before generating again, as generating on its own would loop.
  const [isPaused, setIsPaused] = useState(() => isOutOfSync(location.state));

  const { oceanOffers, hasOffers, hasGenerationFailed, retry } =
    usePreliminaryOfferGeneration({
      isEnabled: !isPaused,
      onOutcome: (outcome) => {
        if (outcome === "no-score") {
          navigate(routes.offer.preliminaryNoScore, { replace: true });
          return;
        }
        if (outcome === "income-rejected") {
          navigate(routes.offer.preliminaryRefinance, { replace: true });
          return;
        }
        if (outcome === "not-available") {
          navigate(routes.offer.preliminaryNotAvailable, { replace: true });
          return;
        }
        // The chat picks the step the backend still misses from fresh facts.
        setIsPaused(true);
        void queryClient.invalidateQueries();
      },
    });

  const isFailed = !hasOffers && (hasGenerationFailed || isPaused);
  const isGenerating = !hasOffers && !isFailed;

  return (
    <ChatTurnLayout
      messageCount={Number(hasOffers) + Number(isFailed)}
      thread={
        <>
          <BotMessage
            lines={chatCopy.preliminaryPreparing}
            isTyped={isPromptTyped}
          />
          <AfterTyping>
            {isGenerating && <TypingIndicator />}
            {hasOffers && (
              <div className={styles.offers}>
                <h2 className={styles.title}>
                  {chatCopy.preliminaryOffersTitle}
                </h2>
                {oceanOffers.map((offer) => (
                  <OceanOfferCard
                    key={offer.id}
                    type={offer.type}
                    amount={offer.amount}
                    numberOfRates={offer.numberOfRates}
                    trackingId={`chat_preliminary_offer_details_${offer.type}`}
                    onDetailsClick={() =>
                      navigate(routes.offer.preliminaryDetails(offer.type))
                    }
                  />
                ))}
              </div>
            )}
            {isFailed && (
              <BotMessage lines={chatCopy.preliminaryFailed} isError />
            )}
          </AfterTyping>
        </>
      }
      actions={
        isFailed && (
          <QuickReplies
            replies={[{ id: RETRY, label: "Încearcă din nou" }]}
            onSelect={() => {
              setIsPaused(false);
              retry();
            }}
          />
        )
      }
    />
  );
}
